'use client'

import Image from 'next/image'
import posthog from 'posthog-js'
import { QRCodeSVG } from 'qrcode.react'
import { useLocale, useTranslations } from 'next-intl'
import { InstallEvents } from '@/lib/analytics/install-events'
import { getBadgeLocaleSuffix } from '@/lib/config/app-install'

/**
 * Shared install-CTA content block: locale-specific App Store + Google
 * Play badges side-by-side, plus a QR code that links to `/get-app?s=qr`.
 *
 * Used by both the settings-page Dialog and the `/get-app` desktop page.
 * Badge hrefs are passed in from the server (which reads
 * `iosAppStoreUrl()` / `playStoreUrl()` from env) — never read in this
 * client component so we can't accidentally fall back to a bad URL.
 *
 * The `source` prop labels the click events so the funnel can attribute
 * conversions per surface.
 *
 * QR is hidden on phones (<sm breakpoint) — you can't scan your own
 * device's screen.
 */
export function GetAppContent({
  iosUrl,
  androidUrl,
  qrTarget,
  source = 'settings-tile',
}: {
  iosUrl: string
  androidUrl: string
  qrTarget: string
  source?: string
}) {
  const locale = useLocale()
  const t = useTranslations('getApp')
  const badgeSuffix = getBadgeLocaleSuffix(locale)

  function handleClick(platform: 'ios' | 'android') {
    try {
      // posthog-js singleton (initialized in instrumentation-client.ts). The
      // prior `window.posthog` lookup silently no-opped — the module isn't
      // exposed on window — so settings-tile clicks never reached PostHog.
      posthog.capture(InstallEvents.CtaClicked, { surface: source, platform })
    } catch {
      /* swallow */
    }
  }

  return (
    <div className="flex flex-col items-center gap-6 pt-2">
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href={iosUrl}
          rel="noopener"
          target="_blank"
          onClick={() => handleClick('ios')}
          className="inline-flex items-center justify-center"
        >
          <Image
            src={`/install-badges/app-store-badge.${badgeSuffix}.svg`}
            alt={t('appStoreAlt')}
            width={156}
            height={52}
            unoptimized
          />
        </a>
        <a
          href={androidUrl}
          rel="noopener"
          target="_blank"
          onClick={() => handleClick('android')}
          className="inline-flex items-center justify-center"
        >
          <Image
            src={`/install-badges/play-badge.${badgeSuffix}.svg`}
            alt={t('playStoreAlt')}
            width={176}
            height={52}
            unoptimized
          />
        </a>
      </div>

      {/* QR is hidden on phones — you can't scan your own device's screen.
          Visible from sm (640px+) on tablet / desktop. */}
      <div className="hidden sm:flex flex-col items-center pt-2 border-t border-border w-full">
        <p className="text-sm text-muted-foreground mb-3 text-center">{t('qrHint')}</p>
        <div className="bg-white p-3 rounded-lg border border-border">
          <QRCodeSVG value={qrTarget} size={148} level="M" includeMargin={false} />
        </div>
      </div>
    </div>
  )
}
